import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import api from '@/services/apiClient';

const useAuthStore = create(
  persist(
    (set, get) => ({
      user: null,
      isAuthenticated: false,
      loading: true,

      login: async (email, password) => {
        const res = await api.post('/auth/login', { email, password });
        const data = res.data || res;
        api.setToken(data.token);
        api.setRefreshToken(data.refreshToken);
        set({ user: data.user, isAuthenticated: true, loading: false });
        return data.user;
      },

      socialLogin: async (idToken, role) => {
        const res = await api.post('/auth/social', { idToken, role });
        const data = res.data || res;
        api.setToken(data.token);
        api.setRefreshToken(data.refreshToken);
        set({ user: data.user, isAuthenticated: true, loading: false });
        return data.user;
      },

      register: async (payload) => {
        const res = await api.post('/auth/register', payload);
        const data = res.data || res;
        api.setToken(data.token);
        api.setRefreshToken(data.refreshToken);
        set({ user: data.user, isAuthenticated: true, loading: false });
        return data.user;
      },

      // Pede o código de redefinição (resposta genérica, sem enumeração).
      forgotPassword: async (email) => {
        const res = await api.post('/auth/forgot-password', { email });
        return res.data || res;
      },

      // Redefine a senha com o código e já entra (recebe tokens).
      resetPassword: async ({ email, code, password }) => {
        const res = await api.post('/auth/reset-password', { email, code, password });
        const data = res.data || res;
        api.setToken(data.token);
        api.setRefreshToken(data.refreshToken);
        set({ user: data.user, isAuthenticated: true, loading: false });
        return data.user;
      },

      logout: () => {
        api.setToken(null);
        api.setRefreshToken(null);
        set({ user: null, isAuthenticated: false, loading: false });
      },

      // Recarrega o usuário a partir do token salvo (boot do app).
      loadProfile: async () => {
        if (!api.token) {
          set({ loading: false, isAuthenticated: false });
          return;
        }
        try {
          const res = await api.get('/auth/me');
          const data = res.data || res;
          set({ user: data.user, isAuthenticated: true, loading: false });
        } catch {
          api.setToken(null);
          api.setRefreshToken(null);
          set({ user: null, isAuthenticated: false, loading: false });
        }
      },

      updateUser: (patch) => set({ user: { ...get().user, ...patch } }),

      // Atualiza o perfil (nome, avatar, telefone, locale, tema) e reflete no estado global.
      updateProfile: async (patch) => {
        const res = await api.patch('/auth/profile', patch);
        const data = res.data || res;
        const updated = data.user || { ...get().user, ...patch };
        set({ user: { ...get().user, ...updated } });
        return updated;
      },

      // Altera a senha (exige a atual).
      changePassword: async (currentPassword, newPassword) => {
        const res = await api.post('/auth/change-password', { currentPassword, newPassword });
        return res.data || res;
      },

      // Exclui a conta e encerra a sessão.
      deleteAccount: async () => {
        await api.delete('/auth/account');
        api.setToken(null);
        api.setRefreshToken(null);
        set({ user: null, isAuthenticated: false, loading: false });
      },

      // Assinaturas do usuário atual.
      mySubscriptions: async () => {
        const res = await api.get('/subscriptions/me');
        return (res.data || res).subscriptions || [];
      },

      // Assina/troca um plano (starter|pro|elite|ai). Reflete plan='ai' no usuário quando aplicável.
      subscribe: async (plan) => {
        const res = await api.post('/subscriptions/subscribe', { plan });
        const data = res.data || res;
        if (plan === 'ai') set({ user: { ...get().user, plan: 'ai' } });
        return data.subscription || data;
      },

      // Cancela uma assinatura por id.
      cancelSubscription: async (id) => {
        const res = await api.post(`/subscriptions/${id}/cancel`, {});
        const data = res.data || res;
        const sub = data.subscription || data;
        if (sub?.type === 'ai') set({ user: { ...get().user, plan: 'free' } });
        return sub;
      },

      hasRole: (...roles) => roles.includes(get().user?.role),
    }),
    {
      name: 'movez-auth',
      partialize: (state) => ({ user: state.user, isAuthenticated: state.isAuthenticated }),
    }
  )
);

export default useAuthStore;
